import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { MessageSquare, Plus, Send, Users, Clock, Eye, Trash2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { deliverMemberNotification } from '@/lib/notificationDelivery';

export default function AdminComunicacao() {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    audience_type: 'Todos',
    group_ids: []
  });

  const queryClient = useQueryClient();

  const { data: messages = [], isLoading } = useQuery({
    queryKey: ['adminMessages'],
    queryFn: () => base44.entities.Message.list('-created_date', 100)
  });

  const { data: groups = [] } = useQuery({
    queryKey: ['groups'],
    queryFn: () => base44.entities.Group.filter({ is_active: true })
  });

  const { data: ministries = [] } = useQuery({
    queryKey: ['ministries'],
    queryFn: () => base44.entities.Ministry.filter({ is_active: true })
  });

  const { data: members = [] } = useQuery({
    queryKey: ['activeMembers'],
    queryFn: () => base44.entities.Member.filter({ status: 'Ativo' })
  });

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.Message.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminMessages'] });
      toast.success('Mensagem enviada com sucesso!');
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Message.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminMessages'] });
      toast.success('Mensagem excluída!');
    }
  });

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      audience_type: 'Todos',
      group_ids: []
    });
    setShowForm(false);
  };

  const isMemberInSelectedGroups = (member) => {
    if (!member || formData.group_ids.length === 0) return false;
    if (formData.group_ids.includes(member.group_id)) return true;

    return groups.some((group) =>
      formData.group_ids.includes(group.id) &&
      Array.isArray(group.member_ids) &&
      group.member_ids.includes(member.id)
    );
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.content) {
      toast.error('Título e conteúdo são obrigatórios.');
      return;
    }

    if (formData.audience_type === 'Grupos' && formData.group_ids.length === 0) {
      toast.error('Selecione pelo menos um grupo.');
      return;
    }

    let audienceName = 'Todos os membros';
    if (formData.audience_type === 'Grupos' && formData.group_ids.length > 0) {
      const selectedGroups = groups.filter(g => formData.group_ids.includes(g.id));
      audienceName = selectedGroups.map(g => g.name).join(', ');
    }

    // Criar a mensagem
    const message = await base44.entities.Message.create({
      title: formData.title,
      content: formData.content,
      audience_type: formData.audience_type,
      audience_id: formData.group_ids.join(','),
      audience_name: audienceName,
      read_by: []
    });

    // Filtrar membros baseado no público
    let targetMembers = members;
    if (formData.audience_type === 'Grupos' && formData.group_ids.length > 0) {
      targetMembers = members.filter(isMemberInSelectedGroups);
    }

    const notificationPromise = deliverMemberNotification({
      recipients: targetMembers.map((member) => ({
        member_id: member.id,
        member_email: member.email,
      })),
      title: formData.title,
      message: formData.content,
      type: 'message',
      referenceId: message.id,
    });

    const emailResults = await Promise.allSettled(targetMembers.map(member =>
      base44.integrations.Core.SendEmail({
        to: member.email,
        subject: `📢 ${formData.title}`,
        body: `
          <h2>${formData.title}</h2>
          <p>${formData.content}</p>
          <hr>
          <p style="color: #666; font-size: 12px;">Enviado para: ${audienceName}</p>
        `
      })
    ));

    await notificationPromise;

    queryClient.invalidateQueries({ queryKey: ['adminMessages'] });
    const failedEmailCount = emailResults.filter((result) => result.status === 'rejected').length;
    if (failedEmailCount > 0) {
      console.error('Falhas ao enviar e-mails de comunicação:', emailResults.filter((result) => result.status === 'rejected'));
      toast.warning(`Mensagem criada, mas ${failedEmailCount} e-mail(s) não foram enviados.`);
    } else {
      toast.success('Mensagem enviada com sucesso para todos os destinatários!');
    }
    resetForm();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader 
            title="Comunicação" 
            subtitle="Enviar mensagens"
            showBack={true}
            action={
              <Button 
                size="sm" 
                variant="secondary"
                onClick={() => setShowForm(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Nova
              </Button>
            }
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {messages.length === 0 ? (
          <EmptyState
            icon={MessageSquare}
            title="Nenhuma mensagem enviada"
            description="Envie comunicados para os membros."
            actionLabel="Enviar Mensagem"
            onAction={() => setShowForm(true)}
          />
        ) : (
          <div className="space-y-4">
            {messages.map((message, index) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl shadow-sm p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{message.title}</h3>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{message.content}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4 pt-3 border-t">
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {format(new Date(message.created_date), "d 'de' MMM", { locale: ptBR })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {message.audience_name || 'Todos'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {message.read_by?.length || 0} lidos
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-600"
                    onClick={() => {
                      if (confirm('Excluir esta mensagem?')) {
                        deleteMutation.mutate(message.id);
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nova Mensagem</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Título/Assunto *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Assunto da mensagem"
              />
            </div>

            <div>
              <Label>Conteúdo *</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Escreva sua mensagem..."
                rows={5}
              />
            </div>

            <div>
              <Label>Destinatários</Label>
              <Select 
                value={formData.audience_type} 
                onValueChange={(v) => setFormData({ ...formData, audience_type: v, group_ids: [] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Todos">Todos os membros</SelectItem>
                  <SelectItem value="Grupos">Grupos específicos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.audience_type === 'Grupos' && groups.length > 0 && (
              <div>
                <Label>Selecione os Grupos</Label>
                <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                  {groups.map((group) => (
                    <label key={group.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.group_ids.includes(group.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ 
                              ...formData, 
                              group_ids: [...formData.group_ids, group.id] 
                            });
                          } else {
                            setFormData({ 
                              ...formData, 
                              group_ids: formData.group_ids.filter(id => id !== group.id) 
                            });
                          }
                        }}
                        className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                      />
                      <span className="text-sm text-gray-700">{group.name}</span>
                    </label>
                  ))}
                </div>
                {formData.group_ids.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.group_ids.length} grupo(s) selecionado(s)
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={resetForm}>
                Cancelar
              </Button>
              <Button 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700" 
                onClick={handleSubmit}
                disabled={createMutation.isPending}
              >
                <Send className="w-4 h-4 mr-2" />
                {createMutation.isPending ? 'Enviando...' : 'Enviar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
